<template>
  <div class="edit-listing-root">
    <div id="layout-wrapper" class="el-layout-wrapper">

      <!-- ========== TOP BAR ========== -->
      <nav class="el-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:16px;">
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" />
            </a>
            <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Edit Listing</span>
          </div>
          <button class="btn btn-sm btn-outline-secondary" @click="$router.back()">Cancel</button>
        </div>
      </nav>

      <!-- ========== LOADING ========== -->
      <div v-if="loading" class="text-center py-5" style="padding-top:100px!important;">
        <div class="spinner-border text-warning" role="status"></div>
        <p class="mt-3" style="color:var(--fm-s,#6B6356);">Loading listing...</p>
      </div>

      <!-- ========== NOT FOUND ========== -->
      <div v-else-if="!listing.id" class="text-center py-5" style="padding-top:100px!important;">
        <i class="mdi mdi-file-alert-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Listing Not Found</h5>
        <p style="color:var(--fm-s,#6B6356);">This listing doesn't exist or you don't have permission to edit it.</p>
        <button class="btn btn-warning btn-sm mt-2" @click="$router.push('/my-listings')">My Listings</button>
      </div>

      <!-- ========== EDIT FORM ========== -->
      <div v-else class="container el-container">
        <div class="row justify-content-center">
          <div class="col-lg-8">

            <!-- Status Banner -->
            <div class="el-status-banner" :class="'status-' + listing.status">
              <i class="mdi" :class="statusIcon"></i>
              <span>Status: <strong>{{ listing.status }}</strong></span>
              <div class="ms-auto d-flex gap-2" v-if="listing.status !== 'sold' && listing.status !== 'removed'">
                <button v-if="listing.status === 'active'" class="btn btn-sm btn-outline-warning" @click="updateStatus('paused')">
                  <i class="mdi mdi-pause me-1"></i> Pause
                </button>
                <button v-if="listing.status === 'paused' || listing.status === 'draft'" class="btn btn-sm btn-warning" @click="updateStatus('active')">
                  <i class="mdi mdi-play me-1"></i> Activate
                </button>
                <button class="btn btn-sm btn-outline-success" @click="updateStatus('sold')">
                  <i class="mdi mdi-check me-1"></i> Mark Sold
                </button>
              </div>
            </div>

            <!-- Current Info (Read-Only) -->
            <div class="el-card">
              <h6 class="el-card-title"><i class="mdi mdi-information-outline me-2"></i>Listing Info <span class="badge bg-dark ms-2" style="font-size:10px;">Read-only</span></h6>
              <div class="el-readonly-row">
                <span class="el-ro-label">Type</span>
                <span class="el-ro-value">{{ listing.listing_type }}</span>
              </div>
              <div class="el-readonly-row">
                <span class="el-ro-label">Species</span>
                <span class="el-ro-value">{{ listing.species_name }}</span>
              </div>
              <div class="el-readonly-row" v-if="listing.breed_name">
                <span class="el-ro-label">Breed</span>
                <span class="el-ro-value">{{ listing.breed_name }}</span>
              </div>
              <div class="el-readonly-row" v-if="listing.county_name">
                <span class="el-ro-label">County</span>
                <span class="el-ro-value">{{ listing.county_name }}</span>
              </div>
              <div class="el-readonly-row">
                <span class="el-ro-label">Created</span>
                <span class="el-ro-value">{{ formatDate(listing.created_at) }}</span>
              </div>
              <div class="el-readonly-row" v-if="listing.view_count">
                <span class="el-ro-label">Views</span>
                <span class="el-ro-value">{{ listing.view_count }}</span>
              </div>
            </div>

            <!-- Editable Fields -->
            <div class="el-card">
              <h6 class="el-card-title"><i class="mdi mdi-pencil me-2"></i>Editable Details</h6>

              <div class="mb-3">
                <label class="el-label">Title</label>
                <input v-model="form.title" type="text" class="form-control el-input" maxlength="300" />
                <div class="el-hint">{{ form.title.length }}/300</div>
              </div>

              <div class="mb-3">
                <label class="el-label">Description</label>
                <textarea v-model="form.description" class="form-control el-input" rows="5"></textarea>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-6">
                  <label class="el-label">Price (KES)</label>
                  <div class="input-group">
                    <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:700;">KES</span>
                    <input v-model="form.price" type="number" step="0.01" class="form-control el-input" min="0" style="border-left:none;" />
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="el-label">Price Type</label>
                  <select v-model="form.priceType" class="form-select el-select">
                    <option value="fixed">Fixed</option>
                    <option value="negotiable">Negotiable</option>
                    <option value="per_unit">Per Unit</option>
                    <option value="per_kg">Per Kg</option>
                    <option value="auction">Auction</option>
                  </select>
                </div>
              </div>

              <div class="row g-3 mb-3" v-if="listing.listing_type !== 'individual'">
                <div class="col-md-6">
                  <label class="el-label">Available Quantity</label>
                  <input v-model="form.availableQuantity" type="number" class="form-control el-input" min="0" />
                </div>
                <div class="col-md-6">
                  <label class="el-label">Min Order Quantity</label>
                  <input v-model="form.minOrderQuantity" type="number" class="form-control el-input" min="1" />
                </div>
              </div>

              <div class="mb-3">
                <label class="el-label">Location Description</label>
                <input v-model="form.locationDescription" type="text" class="form-control el-input" placeholder="e.g. Near Meru town" />
              </div>
            </div>

            <!-- Existing Photos -->
            <div class="el-card" v-if="existingPhotos.length > 0">
              <h6 class="el-card-title"><i class="mdi mdi-camera me-2"></i>Photos</h6>
              <div class="el-photo-grid">
                <div v-for="(ph, pi) in existingPhotos" :key="'ep-'+pi" class="el-photo-slot">
                  <img :src="photoUrl(ph.photo_url)" :alt="'Photo ' + (pi+1)" />
                  <span v-if="ph.is_primary" class="el-photo-primary">Primary</span>
                </div>
              </div>
              <p class="mt-2" style="color:var(--fm-s,#6B6356);font-size:11px;"><i class="mdi mdi-information-outline me-1"></i>To add or remove photos, please contact support or recreate the listing.</p>
            </div>

            <!-- Save / Error -->
            <div v-if="saveError" class="alert alert-danger" style="background:#2D1111;border-color:#5C2020;color:#FF6B6B;font-size:13px;">
              <i class="mdi mdi-alert-circle me-1"></i> {{ saveError }}
            </div>
            <div v-if="saveSuccess" class="alert alert-success" style="background:#0D2818;border-color:#1B5E20;color:#81C784;font-size:13px;">
              <i class="mdi mdi-check-circle me-1"></i> {{ saveSuccess }}
            </div>

            <div class="el-nav-buttons">
              <button class="btn btn-outline-secondary" @click="$router.back()">
                <i class="mdi mdi-arrow-left me-1"></i> Cancel
              </button>
              <div class="flex-fill"></div>
              <button class="btn btn-outline-warning me-2" @click="viewListing">
                <i class="mdi mdi-eye me-1"></i> View
              </button>
              <button class="btn btn-warning" @click="saveListing" :disabled="saving">
                <span v-if="saving"><span class="spinner-border spinner-border-sm me-1"></span> Saving...</span>
                <span v-else><i class="mdi mdi-content-save me-1"></i> Save Changes</span>
              </button>
            </div>

          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style>
html[data-theme="light"] body { background:#F7F5F2!important; color:#1A1A1A!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }

html[data-theme="light"] .edit-listing-root,
html[data-theme="light"] .el-layout-wrapper,
html[data-theme="light"] .edit-listing-root #layout-wrapper,
html[data-theme="light"] .edit-listing-root .el-container {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}

html[data-theme="dark"] .edit-listing-root,
html[data-theme="dark"] .el-layout-wrapper,
html[data-theme="dark"] .edit-listing-root #layout-wrapper,
html[data-theme="dark"] .edit-listing-root .el-container {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}
</style>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
</style>

<style scoped>
.edit-listing-root,
.el-layout-wrapper {
  background:var(--fm-bg2,#0F0F0F)!important;
  min-height:100vh;
  color:var(--fm-h,#F5F0E8)!important;
}

.el-topbar {
  position:sticky; top:0; z-index:100; padding:12px 0;
  background:var(--fm-bg1,#141414)!important; backdrop-filter:blur(10px);
  border-bottom:1px solid var(--fm-br,#1E1E1E);
}

.el-container { padding-top:80px; padding-bottom:80px; max-width:900px; }

.el-status-banner {
  display:flex; align-items:center; gap:10px; padding:12px 16px;
  border-radius:10px; margin-bottom:20px; font-size:13px; color:var(--fm-m,#A09888)!important;
  background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E);
}
.el-status-banner.status-active { border-color:#2E7D32; color:#81C784!important; }
.el-status-banner.status-paused { border-color:#BFA164; color:#f1b44c!important; }
.el-status-banner.status-draft { border-color:#2A2520; color:var(--fm-s,#6B6356)!important; }
.el-status-banner.status-sold { border-color:#1565C0; color:#6CB4EE!important; }

.el-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E); border-radius:12px; padding:24px; margin-bottom:20px; }
.el-card-title { font-size:15px; font-weight:700; color:var(--fm-h,#F5F0E8)!important; margin-bottom:16px; }

.el-readonly-row {
  display:flex; justify-content:space-between; padding:6px 0;
  border-bottom:1px solid var(--fm-br,#1A1A1A); font-size:13px;
}
.el-readonly-row:last-child { border-bottom:none; }
.el-ro-label { color:var(--fm-s,#6B6356)!important; }
.el-ro-value { color:#D4C9B8!important; font-weight:500; }

.el-label { font-size:13px; font-weight:600; color:#D4C9B8!important; margin-bottom:6px; display:block; }
.el-hint { font-size:11px; color:var(--fm-s,#6B6356)!important; margin-top:4px; }
.el-input { background:var(--fm-bg2,#0F0F0F)!important; border:1px solid var(--fm-br,#2A2520); color:var(--fm-h,#F5F0E8)!important; border-radius:8px; }
.el-input:focus { border-color:#BFA164; background:var(--fm-bg2,#0F0F0F)!important; color:var(--fm-h,#F5F0E8)!important; box-shadow:0 0 0 2px rgba(191,161,100,0.15); }
.el-select { background:var(--fm-bg2,#0F0F0F)!important; border:1px solid var(--fm-br,#2A2520); color:var(--fm-h,#F5F0E8)!important; border-radius:8px; }
.el-select:focus { border-color:#BFA164; box-shadow:0 0 0 2px rgba(191,161,100,0.15); }
.el-select option { background:var(--fm-bg1,#141414)!important; color:var(--fm-h,#F5F0E8)!important; }

.el-photo-grid { display:flex; flex-wrap:wrap; gap:10px; }
.el-photo-slot {
  position:relative; width:100px; height:100px; border-radius:8px;
  overflow:hidden; border:1px solid var(--fm-br,#2A2520);
}
.el-photo-slot img { width:100%; height:100%; object-fit:cover; }
.el-photo-primary {
  position:absolute; bottom:0; left:0; right:0; text-align:center;
  background:rgba(191,161,100,0.85)!important; color:#0A0A0A!important; font-size:9px;
  font-weight:700; padding:2px 0;
}

.el-nav-buttons { display:flex; gap:12px; padding:16px 0; }

@media (max-width: 768px) {
  .el-container { padding-top:70px; }
  .el-card { padding:16px; }
  .el-status-banner { flex-wrap:wrap; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "EditListingPage",
  data: function() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver: null,

      loading: true,
      saving: false,
      saveError: '',
      saveSuccess: '',
      listing: {},
      existingPhotos: [],
      form: {
        title: '',
        description: '',
        price: '',
        priceType: 'negotiable',
        availableQuantity: '',
        minOrderQuantity: '1',
        locationDescription: ''
      }
    };
  },
  computed: {
    statusIcon: function() {
      var s = this.listing.status;
      if (s === 'active') return 'mdi-check-circle text-success';
      if (s === 'paused') return 'mdi-pause-circle text-warning';
      if (s === 'sold') return 'mdi-handshake';
      if (s === 'draft') return 'mdi-file-edit-outline';
      if (s === 'expired') return 'mdi-clock-alert-outline';
      return 'mdi-information-outline';
    }
  },
  mounted: function() {
    this.syncTheme();

    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    var userId = sessionStorage.getItem('userId');
    if (!userId) { this.$router.push('/login'); return; }
    var id = this.$route.params.id;
    if (id) this.loadListing(id);
  },
  beforeUnmount: function() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    syncTheme: function() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';
      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
      var el = this.$el;
      if (el) {
        el.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        el.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},

    async loadListing(id) {
      this.loading = true;
      try {
        var userId = sessionStorage.getItem('userId') || '';
        var response = await executeJson({
          function: "getListing",
          listingId: id,
          viewerId: userId
        }, flocker);
        if (response.data.success) {
          this.listing = response.data.data.listing || {};
          this.existingPhotos = this.listing.photos || [];
          this.populateForm();
        }
      } catch (e) {
        console.error('Error loading listing:', e);
      } finally {
        this.loading = false;
      }
    },

    populateForm: function() {
      var l = this.listing;
      this.form.title = l.title || '';
      this.form.description = l.description || '';
      this.form.price = l.price || '';
      this.form.priceType = l.price_type || 'negotiable';
      this.form.availableQuantity = l.available_quantity || '';
      this.form.minOrderQuantity = l.min_order_quantity || '1';
      this.form.locationDescription = l.location_description || '';
    },

    async saveListing() {
      this.saving = true;
      this.saveError = '';
      this.saveSuccess = '';
      try {
        var params = {
          function: "updateListing",
          listingId: this.listing.id,
          title: this.form.title,
          description: this.form.description,
          price: this.form.price,
          priceType: this.form.priceType,
          locationDescription: this.form.locationDescription
        };
        if (this.listing.listing_type !== 'individual') {
          params.availableQuantity = this.form.availableQuantity;
          params.minOrderQuantity = this.form.minOrderQuantity;
        }

        var response = await executeJson(params, flocker);
        if (response.data.success) {
          this.saveSuccess = 'Listing updated successfully!';
          this.listing.title = this.form.title;
          this.listing.description = this.form.description;
          this.listing.price = this.form.price;
          setTimeout(function() { this.saveSuccess = ''; }.bind(this), 3000);
        } else {
          this.saveError = response.data.message || 'Failed to update listing.';
        }
      } catch (e) {
        console.error('Error saving listing:', e);
        this.saveError = 'Something went wrong. Please try again.';
      } finally {
        this.saving = false;
      }
    },

    async updateStatus(newStatus) {
      var label = newStatus === 'sold' ? 'Mark as sold' : newStatus === 'paused' ? 'Pause' : 'Activate';
      if (!confirm(label + ' this listing?')) return;
      try {
        var response = await executeJson({
          function: "updateListing",
          listingId: this.listing.id,
          status: newStatus
        }, flocker);
        if (response.data.success) {
          this.listing.status = newStatus;
          this.saveSuccess = 'Status updated to ' + newStatus + '!';
          setTimeout(function() { this.saveSuccess = ''; }.bind(this), 3000);
        }
      } catch (e) {
        console.error('Error updating status:', e);
      }
    },

    viewListing: function() {
      this.$router.push('/marketplace/listing/' + this.listing.id);
    },

    formatDate: function(dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }
};
</script>
